import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { getLeverageMarginByNotional } from '@ui/utils/trade/getLeverageMarginByNotional';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import { useMinLeverage } from '../AddCollateral/hooks/useMinLeverage';
import { CollateralInput } from '../CollateralInput';
import { getWithdrawCollateralAmountErrorMessage } from '../TradeModule/utils/validateWithdrawCollateralAmount';
import { useMaxRemovableCollateral } from './hooks/useMaxRemovableCollateral';
import { useWithdrawCollateralGasFee } from './hooks/useWithdrawCollateralGasFee';
import { useWithdrawCollateralInput } from './hooks/useWithdrawCollateralInput';
import { useWithdrawCollateralValidation } from './hooks/useWithdrawCollateralValidation';

export const WithdrawCollateralInput = ({
  position,
}: {
  position: Position;
}) => {
  const { t } = useTranslation();

  const payToken = position.collateralToken;
  const payTokenPrice = usePayTokenPriceLatest(payToken);
  const pairMarketPrice = usePriceLatest(position.pair.baseSymbol);
  const maxRemovableResult = useMaxRemovableCollateral({ position });

  const { input, setInput } = useWithdrawCollateralInput();
  const validation = useWithdrawCollateralValidation({ position });
  const minLeverage = useMinLeverage({ position });
  const { data: gas } = useWithdrawCollateralGasFee(position);

  const pairInfos = useTradePairInfos();
  const pairInfo = useMemo(() => {
    return pairInfos?.find(
      (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
    );
  }, [pairInfos, position.pair.address]);

  // Match the contract's tier-by-notional selection so the displayed cap in
  // error messages reflects the actual constraint the user is hitting.
  const maxLeverage = useMemo(() => {
    if (!pairInfo) return 0;
    const notionalUsd = pairMarketPrice
      ? position.size.times(pairMarketPrice)
      : position.size.times(position.entryPrice);
    
return (
      getLeverageMarginByNotional({
        leverageMargins: pairInfo.leverageMargins,
        notionalUsd,
      })?.maxLeverage ?? 0
    );
  }, [pairInfo, pairMarketPrice, position.size, position.entryPrice]);

  const maxRemovable = maxRemovableResult?.maxRemovable ?? ZERO_BIG_NUMBER;

  return (
    <CollateralInput
      balanceLabelPrefix={`${t('common.Max')}: `}
      gasFee={gas}
      getErrorMessage={() =>
        getWithdrawCollateralAmountErrorMessage({
          t,
          error: validation.error,
          maxLeverage,
          minLeverage,
          maxRemovable,
          leverageAfterMaxRemoval: maxRemovableResult?.leverageAfterMaxRemoval,
          symbol: payToken?.displaySymbol,
        })
      }
      onInput={(value) => {
        setInput({ value });
      }}
      tokenBalance={maxRemovable}
      tokenPrice={payTokenPrice}
      token={payToken}
      value={input.value}
      leverageSetterEnabled={false}
    />
  );
};
